export default class CssProperties {
  /**
   * Retrieves the value of a CSS property from a specified element.
   * If an array of units is provided via `replaceUnits`, the method checks whether the value ends with
   * one of these units. If so, the unit is removed and the numeric part is returned as a float.
   * If no element is provided, the document root element (document.documentElement) is used by default.
   *
   * @param {string} name - The name of the CSS property (e.g., '--main-color' or '--container-width').
   * @param {string[]} [replaceUnits='px'] - An array of CSS units to remove (e.g., ['px', 'em', 'rem', 'ms', 's']).
   * @param {HTMLElement} [element=document.documentElement] - The HTML element from which the CSS property is read.
   * @returns {(number|string|null)} - The numeric value if a unit match was found and stripped,
   *                                   or the original value as a trimmed string. Returns null if the element or property is not valid.
   */
  static getCssProperty(name, replaceUnits = ['px'], element = document.documentElement) {
    if (!element || !window.getComputedStyle) {
      return null;
    }

    const value = getComputedStyle(element).getPropertyValue(name).trim();

    for (const unit of replaceUnits) {
      if (value.endsWith(unit)) {
        const numeric = parseFloat(value.replace(unit, '').trim());
        if (isNaN(numeric)) {
          return null;
        }

        return unit === 's' ? numeric * 1000 : numeric; // Convert seconds to milliseconds
      }
    }

    return value; // Return original trimmed value if no matching unit was found
  }


  /**
   * Retrieves the value of a CSS property (in pixels) from a specified element.
   * If the property is defined in pixels (e.g., '20px'), the 'px' unit is stripped
   * and the numeric value is returned. If the property has a different unit,
   * the original value is returned unchanged. If no element is provided,
   * the document root element (document.documentElement) is used by default.
   *
   *
   * @param {string} name - The name of the CSS property (e.g., '--main-color' or 'width').
   * @param {HTMLElement} [element=document.documentElement] - The HTML element from which the CSS property is read. Defaults to the document root element.
   * @returns {(number|string|null)} - The numeric value if the property is in pixels, or the original value as a string if it is in another unit. Returns null if the element or property is not valid.
   */
  static getCssPixelProperty(name, element) {
    if (!element) {
      element = document.documentElement;
    }

    // Check if the element exists and getComputedStyle is available
    if (window.getComputedStyle && element) {
      const value = getComputedStyle(element).getPropertyValue(name);
      if (value.includes('px')) {
        return parseFloat(value.replace('px', '')); // Remove 'px' and convert to a number
      }
      if (value.includes('ms')) {
        return parseFloat(value.replace('ms', '')); // Remove 'ms' and convert to a number
      }
      return value; // Return the original value if it's not in 'px'
    }

    return null; // Return null if the element or property name is not valid
  }

  /**
   * Retrieves a CSS time property's value from a specified element.
   * If the value ends with 'ms' or 's', the unit is removed and the numeric part is returned as a float.
   * If no element is provided, the document root element (document.documentElement) is used by default.
   *
   * This is a convenience wrapper around `getCssProperty()` with fixed units ['ms', 's'].
   *
   * @param {string} name - The name of the CSS time property (e.g., '--animation-delay').
   * @param {HTMLElement} [element=document.documentElement] - The HTML element from which the CSS property is read.
   * @returns {(number|string|null)} - The numeric value (unit stripped) if a known time unit was found,
   *                                   the raw value as a string otherwise, or null if invalid.
   */
  static getCssTimeProperty(name, element = document.documentElement) {
    return CssProperties.getCssProperty(name, ['ms', 's'], element);
  }
}
